import { useState, useRef, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Camera } from "lucide-react";
import { ImageCropDialog } from "@/components/ImageCropDialog";

const photoUrl = (id, v) => `${process.env.REACT_APP_BACKEND_URL}/api/members/${id}/photo?v=${v}`;

export const EditProfileDialog = ({ open, onOpenChange, profile, onUpdated }) => {
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [preview, setPreview] = useState(null);
  const [fileObj, setFileObj] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rawImage, setRawImage] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPhone(profile?.phone || "");
      setAddress(profile?.address || "");
      setPreview(null);
      setFileObj(null);
    }
  }, [open, profile]);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(f.type)) {
      toast.error("Please upload a JPG, PNG, WEBP or GIF image");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { setRawImage(reader.result); setCropOpen(true); };
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const onCropped = (file) => {
    setFileObj(file);
    setPreview(URL.createObjectURL(file));
    setCropOpen(false);
    setRawImage(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/me/profile", { phone, address });
      if (fileObj) {
        const fd = new FormData();
        fd.append("file", fileObj);
        await api.post("/me/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Profile updated");
      onUpdated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update profile");
    } finally {
      setSaving(false);
    }
  };

  const currentPhoto = preview || (profile?.photo_path ? photoUrl(profile.id, profile.photo_path) : null);

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="edit-profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Edit My Profile</DialogTitle>
          <DialogDescription>Update your contact details and profile photo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div className="flex flex-col items-center gap-3">
            <div className="relative">
              <div className="h-24 w-24 rounded-full overflow-hidden flex items-center justify-center text-white text-2xl font-semibold bg-[#2D5A27]"
                style={{ backgroundColor: profile?.avatar_color || "#2D5A27" }}>
                {currentPhoto
                  ? <img src={currentPhoto} alt="Profile" className="h-full w-full object-cover" />
                  : `${profile?.first_name?.[0] || ""}${profile?.last_name?.[0] || ""}`}
              </div>
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="pick-photo-button"
                className="absolute -bottom-1 -right-1 h-9 w-9 rounded-full bg-[#D97706] hover:bg-[#B45309] text-white flex items-center justify-center shadow">
                <Camera className="h-4 w-4" />
              </button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickFile} data-testid="photo-file-input" />
            <p className="text-xs text-[#7A8A78]">JPG, PNG, WEBP or GIF · up to 5MB</p>
          </div>
          <div className="space-y-1.5">
            <Label>Phone</Label>
            <Input data-testid="profile-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(09) 123-4567" />
          </div>
          <div className="space-y-1.5">
            <Label>Address</Label>
            <Input data-testid="profile-address-input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="123 Queen St, Auckland" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-profile-button"
            className="bg-[#2D5A27] hover:bg-[#23471F] text-white">
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <ImageCropDialog open={cropOpen} image={rawImage} onCancel={() => { setCropOpen(false); setRawImage(null); }} onCropped={onCropped} />
    </>
  );
};
